import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { RealtimeChannel } from '@supabase/supabase-js'

export interface ActivityEvent {
  id: string
  actor_id: string
  actor_name: string
  action: string
  resource_type: string
  resource_id: string
  created_at: string
  metadata: Record<string, any>
}

export function useActivityFeed(limit: number = 50) {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const seen = new Set<string>()

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true)
      // Get activity events
      const { data: events, error: eventsError } = await supabase
        .from('activity_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (eventsError) throw eventsError

      // Get unique actor IDs
      const actorIds = [...new Set(events?.map(e => e.actor_id).filter(Boolean) || [])]
      
      // Fetch user profiles for actors
      let profiles: any[] = []
      if (actorIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, display_name, username, avatar_url')
          .in('id', actorIds)
        
        if (profilesError) throw profilesError
        profiles = profilesData || []
      }

      // Create profile lookup map
      const profileMap = new Map(profiles.map(p => [p.id, p]))

      const formatted: ActivityEvent[] = (events || []).map((event: any) => ({
        id: event.id,
        actor_id: event.actor_id,
        actor_name: profileMap.get(event.actor_id)?.display_name || 
                   profileMap.get(event.actor_id)?.username || 
                   'Anonymous',
        action: event.action,
        resource_type: event.resource_type,
        resource_id: event.resource_id,
        created_at: event.created_at,
        metadata: event.metadata || {}
      }))

      setEvents(formatted)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    loadHistory()

    let channel: RealtimeChannel
    channel = supabase.channel('activity-feed')
    channel
      .on('broadcast', { event: 'activity_event' }, ({ payload }) => {
        const e: ActivityEvent = {
          id: payload.id,
          actor_id: payload.actor_id,
          actor_name: payload.actor_name,
          action: payload.action,
          resource_type: payload.resource_type,
          resource_id: payload.resource_id,
          created_at: payload.created_at,
          metadata: payload.metadata || {}
        }
        if (seen.has(e.id)) return
        seen.add(e.id)
        setEvents((prev) => [e, ...prev].slice(0, limit))
      })
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [loadHistory, limit])

  return { events, loading, error, refresh: loadHistory }
}


